import { useState } from 'react'

import {
  Alert,
  Box,
  Text,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import { ProjectManagementProjectRoleRoleEnum } from 'generated/resources/graphql'
import { xor } from 'lodash'

import { EmployeeForSelectFragmentNoAccess } from 'modules/core/components/EntitySelectWrapper/Entities/Employees/types'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { CreateProjectProps } from '../types'
import { CreateProjectRolesSelector } from './CreateProjectRolesSelector'

export const RolesSelectionStep = ({
  data,
  onDataChanged,
}: WizardStepRenderParams<CreateProjectProps>) => {
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeForSelectFragmentNoAccess[]>(
    []
  )

  useOnNext(() => {
    track(trackingEvents.projectManagement.wizard.projectRolesProjectWizard, {
      owners: data.projectRoles.map((pr) => pr.role === 'owner').length,
      editors: data.projectRoles.map((pr) => pr.role === 'editor').length,
      viewers: data.projectRoles.map((pr) => pr.role === 'viewer').length,
    })
  })

  const onUpdateRow = (
    employee: EmployeeForSelectFragmentNoAccess,
    role: ProjectManagementProjectRoleRoleEnum
  ) => {
    const index = data.projectRoles.findIndex((pr) => pr.employee.id === employee.id)
    if (index !== -1) {
      const projectRole = data.projectRoles[index]
      if (projectRole) {
        projectRole.role = role
      }
    } else {
      data.projectRoles.push({
        role,
        employee,
      })
    }

    onDataChanged({ ...data })

    setSelectedEmployees(xor(selectedEmployees, [employee]))
  }

  const onDeleteRow = (employee: EmployeeForSelectFragmentNoAccess) => {
    data.projectRoles = data.projectRoles.filter((pr) => pr.employee.id !== employee.id)

    setSelectedEmployees(selectedEmployees.filter((se) => se.id !== employee.id))
  }

  const disabledEmployeeIds = data.projectRoles.map((pr) => pr.employee.id)

  const onSelectEmployees = (newEmployees: EmployeeForSelectFragmentNoAccess[]) => {
    setSelectedEmployees(newEmployees)
  }

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Text.Header2 weight="semibold">
          {i18n.t('project_management.modals.create_project.wizard.step_two.header')}
        </Text.Header2>
        <CreateProjectRolesSelector
          onUpdateRow={onUpdateRow}
          onDeleteRow={onDeleteRow}
          projectRoles={data.projectRoles}
          excludeEmployeeIds={disabledEmployeeIds}
          setSelectedEmployees={setSelectedEmployees}
          selectedEmployees={selectedEmployees}
          onSelectEmployees={onSelectEmployees}
        />
      </Box>
      <Box
        flexDirection="column"
        justifyContent="flexStart"
        maxWidth="s200"
        gap="s8"
        paddingTop="s64"
      >
        <Alert color="info">
          {i18n.t('project_management.modals.create_project.wizard.step_two.alert')}
        </Alert>
      </Box>
    </Box>
  )
}
